import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Checkbox, Flex, Form, Input, Space, Tag } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import useForm from '../../../hooks/utility/useForm';
import useApiClient from '../../../hooks/api/useApiClient';
import useFeedbackLabel from '../../../hooks/ui/useFeedbackLabel';
import GooglePlacesSearch from '../../form/address/GooglePlacesSearch';
import { useGetServiceContractQuery } from '../../../store/api-slices';
import YousignInPersonSigning from './YousignInPersonSigning';

const { TextArea } = Input;

const SIGNATURE_STATUS = Object.freeze({
  Done: 'done',
  Declined: 'declined',
});

export default function YousignSignStep({ contract, backButton, onSuccess, isSkippable, skip, skipping }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SignStep' });

  const { ServiceContractsManager } = useApiClient();

  const [isSignInited, setIsSignInited] = useState(false);
  const [signatureLink, setSignatureLink] = useState(null);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const { data, update } = useForm({
    agreement: false,
    signer_first_name: contract?.signer_first_name ?? '',
    signer_last_name: contract?.signer_last_name ?? '',
    recipient_email: contract?.recipient_email ?? '',
    additional_locations: contract?.additional_locations ?? [],
    special_agreements: contract?.special_agreements ?? '',
  });

  const [loading, setLoading] = useState(false);
  const [loadingOngoingSignLink, setOngogingSignLink] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  // This namespace holds the `form.<field>.label` keys for every field posted to
  // init-sign, so `FeedbackLabel` can name the fields the backend rejected.
  const { setErrorFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackLabel({ t });

  // Once the signature is done, the backend processes it asynchronously (Yousign
  // webhook updates `signed_at`/status). Poll the contract every 5s so the freshly
  // refetched contract — not the stale prop — is what we hand off on continue.
  const [signedContract, setSignedContract] = useState(null);
  const isSigned = signatureStatus === SIGNATURE_STATUS.Done;
  const { data: refetchedContract } = useGetServiceContractQuery(
    { apiManager: ServiceContractsManager },
    { skip: !isSigned || Boolean(signedContract?.signed_at), pollingInterval: 5000, refetchOnMountOrArgChange: true },
  );

  // Stop polling and enable continue once the refetched contract has `signed_at`.
  useEffect(() => {
    if (refetchedContract?.signed_at) {
      setSignedContract(refetchedContract);
    }
  }, [refetchedContract]);

  const addAdditionalLocation = (place) => {
    const name = place?.name?.split(',')[0] ?? place?.name;
    const address = place?.formatted_address ?? null;
    const placeId = place?.place_id ?? null;

    if (!name || !address || !placeId) {
      return;
    }

    const exists = data.additional_locations.some(
      (location) =>
        (placeId && location.place_id === placeId) || (location.name === name && location.address === address),
    );

    if (!exists) {
      update('additional_locations', [...data.additional_locations, { name, address, place_id: placeId }]);
    }

    setLocationSearch('');
  };

  const removeAdditionalLocation = (index) =>
    update(
      'additional_locations',
      data.additional_locations.filter((_, i) => i !== index),
    );

  useEffect(() => {
    if (!contract?.id) {
      return;
    }

    setOngogingSignLink(true);

    ServiceContractsManager.getSignatureLink(contract.id)
      .then((response) => {
        if (!response?.success || !response.data) {
          return;
        }

        setSignatureLink(response.data);
        setIsSignInited(true);
      })
      .finally(() => setOngogingSignLink(false));
  }, [contract?.id]);

  const initSign = async () => {
    resetFeedback();
    setLoading(true);
    toast.promise(
      new Promise((resolve, reject) => {
        ServiceContractsManager.initSign(contract?.id, data)
          .then((response) => {
            if (!response?.success) {
              // Failed responses carry the backend body in `data` — surface its
              // validation errors instead of leaving only the generic toast.
              setErrorFeedback(response?.data);

              return reject();
            }

            setSignatureLink(response.data);
            setIsSignInited(true);

            return resolve();
          })
          .finally(() => setLoading(false));
      }),
      {
        pending: t('toasts.contract_signing_pending'),
        success: t('toasts.contract_got_signed'),
        error: t('toasts.failed_to_sign_contract'),
      },
    );
  };

  return (
    <div className="mt-medium relative">
      <h2 className="mb-2">{t('header')}</h2>
      <p className="mb-4">{t('description')}</p>
      {isSignInited && (
        <>
          {(signatureLink || backButton) && (
            <Flex justify="space-between" align="flex-end" gap="small" wrap className="mb-medium">
              {signatureLink ? (
                <div className="flex-1 min-w-[280px]">
                  <p className="mb-2 font-medium">{t('signature_link.label')}</p>
                  <Space.Compact className="w-full max-w-xl">
                    <Input readOnly value={signatureLink} />
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(signatureLink);
                        toast.success(t('signature_link.copied'));
                      }}
                    >
                      {t('signature_link.copy')}
                    </Button>
                  </Space.Compact>
                </div>
              ) : (
                <span />
              )}
              {backButton}
            </Flex>
          )}
          <YousignInPersonSigning
            contractId={contract?.id}
            signatureLink={signatureLink}
            onSignatureDone={() => setSignatureStatus(SIGNATURE_STATUS.Done)}
            onDeclined={() => setSignatureStatus(SIGNATURE_STATUS.Declined)}
          />
        </>
      )}
      {!isSignInited && !loadingOngoingSignLink && (
        <>
          {renderFeedbackLabel}
          <Form layout="vertical" className="mt-medium">
            <Form.Item label={t('form.signer_first_name.label')}>
              <Input
                value={data.signer_first_name}
                id="signer_first_name"
                onChange={(e) => update('signer_first_name', e.target.value)}
                placeholder={t('form.signer_first_name.placeholder')}
                className="w-3/5"
              />
            </Form.Item>
            <Form.Item label={t('form.signer_last_name.label')}>
              <Input
                value={data.signer_last_name}
                id="signer_last_name"
                onChange={(e) => update('signer_last_name', e.target.value)}
                placeholder={t('form.signer_last_name.placeholder')}
                className="w-3/5"
              />
            </Form.Item>
            <Form.Item label={t('form.recipient_email.label')}>
              <Input
                type="email"
                value={data.recipient_email}
                id="recipient_email"
                onChange={(e) => update('recipient_email', e.target.value)}
                placeholder={t('form.recipient_email.placeholder')}
                className="w-3/5"
              />
            </Form.Item>
            <Form.Item label={t('form.additional_locations.label')}>
              <div className="w-3/5">
                <GooglePlacesSearch
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  onPlaceSelected={addAdditionalLocation}
                  placeholder={t('form.additional_locations.placeholder')}
                  options={{
                    fields: ['name', 'formatted_address', 'place_id'],
                    types: ['bakery', 'bar', 'cafe', 'restaurant', 'food'],
                  }}
                />
              </div>
              {data.additional_locations.length > 0 && (
                <Flex wrap gap="small" className="mt-mini">
                  {data.additional_locations.map((location, index) => (
                    <Tag
                      key={location.place_id ?? `${location.name}-${location.address}`}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        removeAdditionalLocation(index);
                      }}
                      className="m-0"
                    >
                      {location.address ? `${location.name}, ${location.address}` : location.name}
                    </Tag>
                  ))}
                </Flex>
              )}
            </Form.Item>
            <Form.Item label={t('form.special_agreements.label')}>
              <TextArea
                value={data.special_agreements}
                id="special_agreements"
                onChange={(e) => update('special_agreements', e.target.value)}
                placeholder={t('form.special_agreements.placeholder')}
                autoSize={{ minRows: 3, maxRows: 6 }}
                className="w-3/5"
              />
            </Form.Item>
            <Form.Item>
              <Checkbox id="agreement" checked={data.agreement} onChange={(e) => update('agreement', e.target.checked)}>
                {t('form.agreement.label')}
              </Checkbox>
            </Form.Item>
          </Form>
          <Flex justify="space-between" gap="small">
            {isSkippable && (
              <Button color="default" variant="solid" onClick={skip} loading={skipping}>
                {t('buttons.skip')}
              </Button>
            )}
            <Flex justify="end" gap="small" className="ml-auto">
              {backButton}
              <Button
                type="primary"
                loading={loading}
                disabled={
                  !data.agreement ||
                  !data.signer_first_name?.trim() ||
                  !data.signer_last_name?.trim() ||
                  !data.recipient_email?.trim()
                }
                onClick={initSign}
              >
                {t('buttons.init_sign')}
              </Button>
            </Flex>
          </Flex>
        </>
      )}
      {signatureStatus === SIGNATURE_STATUS.Done && (
        <Flex justify="end" gap="small" className="mt-4">
          <Button
            type="primary"
            loading={!signedContract?.signed_at}
            onClick={() => signedContract && onSuccess(signedContract)}
          >
            {t('buttons.continue')}
          </Button>
        </Flex>
      )}
      {signatureStatus === SIGNATURE_STATUS.Declined && (
        <Flex justify="end" gap="small" className="mt-4">
          <Button
            onClick={() => {
              setIsSignInited(false);
              setSignatureLink(null);
              setSignatureStatus(null);
            }}
          >
            {t('buttons.back')}
          </Button>
        </Flex>
      )}
    </div>
  );
}
